// Instance Admin → Image Generation: the PLATFORM image key.
//
// What an organization spends its AI credits on when its functional admin
// picks "AI Credit" under System administration → AI → Image generation, and
// what a builder without their own image key falls back to. With nothing set
// here, the platform AI key (AI Settings tab) serves when its provider can make
// images — the screen says which one is live.

import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  clearPlatformImageGen, getPlatformImageGen, testPlatformImageGen, updatePlatformImageGen,
  type PlatformImageGenSettings,
} from '@/api/instanceAdmin'
import { Field, InfoBox, errText } from './adminUi'

const PROVIDER_LABEL: Record<string, string> = { openai: 'OpenAI', google: 'Google (Imagen)' }

export default function ImageGenerationTab() {
  const [s, setS] = useState<PlatformImageGenSettings | null>(null)
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')
  const [apiKey, setApiKey] = useState('')          // blank = leave the stored one
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'' | 'save' | 'test' | 'clear'>('')
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  const adopt = (v: PlatformImageGenSettings) => {
    setS(v)
    setProvider(v.provider || '')
    setModel(v.model || '')
    setApiKey('')
  }

  useEffect(() => {
    getPlatformImageGen()
      .then(adopt)
      .catch((e) => toast.error(errText(e, 'Failed to load the image-generation settings')))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const models = (provider && s.models[provider]) || []
  const save = async () => {
    setBusy('save'); setResult(null)
    try {
      adopt(await updatePlatformImageGen({
        provider, model: model.trim(),
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      }))
      toast.success('Image-generation settings saved')
    } catch (e) {
      toast.error(errText(e, 'Failed to save'))
    } finally { setBusy('') }
  }
  const test = async () => {
    setBusy('test'); setResult(null)
    try {
      setResult(await testPlatformImageGen({
        provider: provider || undefined, model: model.trim() || undefined,
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      }))
    } catch (e) {
      setResult({ ok: false, message: errText(e, 'Check failed') })
    } finally { setBusy('') }
  }
  const clear = async () => {
    if (!window.confirm(
      'Remove the platform image key?\n\nOrganizations on AI Credit then fall back '
      + 'to the platform AI key when it can make images (OpenAI / Google), and '
      + 'otherwise stop generating pictures. Organizations on their own key are unaffected.',
    )) return
    setBusy('clear'); setResult(null)
    try {
      adopt(await clearPlatformImageGen())
      toast.success('Platform image key removed')
    } catch (e) {
      toast.error(errText(e, 'Failed to clear'))
    } finally { setBusy('') }
  }

  const live = s.effective_source === 'image'
    ? `Live: ${PROVIDER_LABEL[s.effective_provider] || s.effective_provider} · ${s.effective_model} (this setting).`
    : s.effective_source === 'bundle'
      ? `Live: ${PROVIDER_LABEL[s.effective_provider] || s.effective_provider} · ${s.effective_model}, derived from the platform AI key on the AI Settings tab.`
      : 'Not available — AI Credit cannot generate images until a key is set here (or the platform AI key is OpenAI / Google).'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 560 }}>
      <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Platform image generation</h3>
      <InfoBox>
        The provider, model and key that generate pictures on this instance&apos;s
        account. An application&apos;s functional admin chooses, under{' '}
        <strong>System administration → AI → Image generation</strong>, between
        this key — paid with their organization&apos;s <strong>AI credits</strong>
        {' '}— and their own OpenAI / Google key. Builders with no image key of
        their own fall back to it too, within their plan.
        <div style={{ marginTop: 6, fontWeight: 600 }}>{live}</div>
      </InfoBox>

      <Field label="Provider">
        <select value={provider} onChange={(e) => { setProvider(e.target.value); setModel(''); setResult(null) }} style={{ width: '100%' }}>
          <option value="">Not set — use the platform AI key when it can make images</option>
          {s.providers.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p] || p}</option>)}
        </select>
      </Field>

      {provider && (
        <Field label="Model">
          <input
            list="platform-image-models" value={model}
            onChange={(e) => { setModel(e.target.value); setResult(null) }}
            placeholder={s.default_models[provider] || ''}
            style={{ width: '100%' }}
          />
          <datalist id="platform-image-models">
            {models.map((m) => <option key={m} value={m} />)}
          </datalist>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
            Blank = {s.default_models[provider]}. Prices come from Billing → Credit conversion.
          </p>
        </Field>
      )}

      {provider && (
        <Field label={`API key ${s.has_key ? '(configured — leave blank to keep)' : '(not set)'}`}>
          <input
            type="password" value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); setResult(null) }}
            placeholder={s.has_key ? '••••••••' : provider === 'openai' ? 'sk-…' : 'AIza…'}
            autoComplete="new-password" spellCheck={false} style={{ width: '100%' }}
          />
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0' }}>
            Encrypted at rest and never returned to this screen.
          </p>
        </Field>
      )}

      {result && (
        <div style={{
          padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.9125rem',
          background: result.ok ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
          color: result.ok ? '#15803d' : '#b91c1c',
        }}>{result.message}</div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={save} disabled={!!busy}>
          {busy === 'save' ? 'Saving…' : 'Save image settings'}
        </button>
        <button className="btn btn-secondary" onClick={test}
          disabled={!!busy || (!s.available && !apiKey.trim())}
          title="Checks the key can reach the model — a free lookup, no picture is generated">
          {busy === 'test' ? 'Checking…' : 'Test key'}
        </button>
        {(s.provider || s.has_key) && (
          <button className="btn btn-secondary" onClick={clear} disabled={!!busy}>
            {busy === 'clear' ? 'Removing…' : 'Remove'}
          </button>
        )}
      </div>
    </div>
  )
}
